import { Reminder, TriggerRelation } from '../../../types/task';
import { TIME_UNIT_OPTIONS } from '../components/ios/IOSReminderComponents';
import { parseISO, isValid, format } from 'date-fns';
import { toZonedTime, formatInTimeZone, fromZonedTime } from 'date-fns-tz';

/**
 * 将秒数转换为数值和单位
 */
export const secondsToValueUnit = (seconds: number): { value: number; unit: 'minutes' | 'hours' | 'days' } => {
  const absSeconds = Math.abs(seconds);

  // 优先使用最大的整数单位
  if (absSeconds >= 86400 && absSeconds % 86400 === 0) {
    return { value: absSeconds / 86400, unit: 'days' };
  }
  if (absSeconds >= 3600 && absSeconds % 3600 === 0) {
    return { value: absSeconds / 3600, unit: 'hours' };
  }
  // 默认使用分钟，即使不是整数也四舍五入
  return { value: Math.round(absSeconds / 60), unit: 'minutes' };
};

/**
 * 将数值和单位转换为秒数
 */
export const valueUnitToSeconds = (value: number, unit: string, isBefore: boolean): number => {
  const unitInfo = TIME_UNIT_OPTIONS.find(u => u.value === unit);
  const factor = unitInfo ? unitInfo.factor : 60;
  const seconds = value * factor;
  return isBefore ? -seconds : seconds;
};

/**
 * 格式化绝对时间为输入框所需格式
 */
export const formatAbsoluteTimeForInput = (isoString?: string): string => {
  if (!isoString) return '';
  try {
    const date = parseISO(isoString);
    if (!isValid(date)) return '';
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const localDate = toZonedTime(date, userTimeZone);
    return format(localDate, "yyyy-MM-dd'T'HH:mm");
  } catch (error) {
    console.error("Error formatting absolute date for input:", error);
    return '';
  }
};

/**
 * 将输入框的本地时间转换为绝对时间
 */
export const parseInputToAbsoluteTime = (localString: string): string | undefined => {
  if (!localString) return undefined;
  try {
    const localDate = new Date(localString);
    if (!isValid(localDate)) return undefined;
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const utcDate = fromZonedTime(localDate, userTimeZone);
    return utcDate.toISOString().replace(/\.\d{3}Z$/, 'Z');
  } catch (error) {
    console.error("Error parsing local input to absolute UTC:", error);
    return undefined;
  }
};

/**
 * 获取提醒的描述文本
 */
export const getReminderDescription = (reminder: Reminder, t: Function): string => {
  const { trigger } = reminder;

  if (trigger.type === 'ABSOLUTE') {
    if (!trigger.absoluteTime) return t('v3/ios:remindersEditor.types.absolute', '指定时间');
    try {
      const date = parseISO(trigger.absoluteTime);
      if (!isValid(date)) return t('v3/ios:remindersEditor.invalidAbsoluteTime', '无效的指定时间');
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return formatInTimeZone(date, userTimeZone, 'yyyy-MM-dd HH:mm') +
             ` (${userTimeZone.split('/').pop()?.replace('_', ' ') || userTimeZone})`;
    } catch {
      return t('v3/ios:remindersEditor.invalidAbsoluteTime', '无效的指定时间');
    }
  } else if (trigger.type === 'RELATIVE') {
    if (trigger.offsetSeconds === undefined || trigger.related === undefined) {
      return t('v3/ios:remindersEditor.incompleteRelative', '未完成的相对时间');
    }

    let relationText = '';
    switch (trigger.related) {
      case TriggerRelation.START:
        relationText = t('v3/ios:remindersEditor.relativeToOptions.START', '开始时间');
        break;
      case TriggerRelation.CALCULATED_END:
        relationText = t('v3/ios:remindersEditor.relativeToOptions.CALCULATED_END', '计算结束时间');
        break;
      case TriggerRelation.DUE:
        relationText = t('v3/ios:remindersEditor.relativeToOptions.DUE', '截止时间');
        break;
      default:
        relationText = '?';
    }

    const offset = trigger.offsetSeconds;
    const isBefore = offset < 0;
    const absOffset = Math.abs(offset);
    const { value, unit } = secondsToValueUnit(absOffset);
    const unitText = TIME_UNIT_OPTIONS.find(u => u.value === unit)?.label || '?';
    const directionText = isBefore
      ? t('v3/ios:remindersEditor.direction.before', '前')
      : t('v3/ios:remindersEditor.direction.after', '后');

    return `${relationText} ${directionText} ${value}${unitText}`;
  } else {
    return t('v3/ios:remindersEditor.unknownType', '未知提醒类型');
  }
};

/**
 * 验证提醒设置是否有效
 */
export const validateReminder = (reminder: Reminder): { isValid: boolean; error?: string } => {
  if (!reminder.action) {
    return { isValid: false, error: '缺少提醒动作' };
  }

  if (!reminder.methods || reminder.methods.length === 0) {
    return { isValid: false, error: '至少需要一种提醒方式' };
  }

  const { trigger } = reminder;

  if (trigger.type === 'ABSOLUTE') {
    if (!trigger.absoluteTime) {
      return { isValid: false, error: '绝对时间提醒需要指定时间' };
    }
    try {
      const date = parseISO(trigger.absoluteTime);
      if (!isValid(date)) {
        return { isValid: false, error: '无效的绝对时间格式' };
      }
    } catch {
      return { isValid: false, error: '无效的绝对时间格式' };
    }
  } else if (trigger.type === 'RELATIVE') {
    if (trigger.offsetSeconds === undefined) {
      return { isValid: false, error: '相对时间提醒需要指定偏移量' };
    }
    if (!trigger.related || !Object.values(TriggerRelation).includes(trigger.related)) {
      return { isValid: false, error: '相对时间提醒需要指定关联点' };
    }
  } else {
    return { isValid: false, error: '未知的触发类型' };
  }

  return { isValid: true };
};

import { createDefaultReminderMethods, getUserPreferredReminderMethods } from '../../../types/notificationMapping';

/**
 * 创建默认的提醒对象
 * @param userPreferences 用户偏好设置（可选）
 */
export const createDefaultReminder = (userPreferences?: any): Reminder => {
  return {
    action: 'DISPLAY',
    trigger: {
      type: 'RELATIVE',
      related: TriggerRelation.START,
      offsetSeconds: -900 // 默认提前15分钟
    },
    description: '任务提醒',
    methods: getUserPreferredReminderMethods(userPreferences) // 使用用户偏好的提醒方式
  };
};

import { formatReminderMethods as formatReminderMethodsBase, ReminderMethodType } from '../../../types/notificationMapping';

/**
 * 格式化提醒方式列表为显示文本
 */
export const formatReminderMethods = (methods: string[]): string => {
  return formatReminderMethodsBase(methods as ReminderMethodType[]);
};
